import { useState, useEffect, useMemo } from 'react';
import { notify } from 'messages';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import Button from 'components/base/Button';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose } from '@fortawesome/free-solid-svg-icons';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { updateConfig, createConfig } from '../../../../redux/slices/configSlice';

import { CONFIG_TYPE_OPTIONS, resolveConfigType } from './configTypeRegistry';
import { isReservedConfigKey, RESERVED_CONFIG_KEY_MESSAGE } from './configKeyValidation';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

interface EditConfigItemProps {
  configIndex: number | null;
  isNew?: boolean;
  handleClose: () => void;
}

const EditConfigItem: React.FC<EditConfigItemProps> = ({ configIndex, isNew = false, handleClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentConfig = useAppSelector((state) => (configIndex !== null ? state.config.items[configIndex] : null));

  const [selectedKey, setSelectedKeyState] = useState<string>(isNew ? '' : currentConfig?.key || '');
  const [jsonValid, setJsonValid] = useState<boolean>(true);
  const [formData, setFormData] = useState<any>(currentConfig?.value || {});
  const [timezoneValidationMessage, setTimezoneValidationMessage] = useState<string>('');
  const [keyValidationMessage, setKeyValidationMessage] = useState<string>('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setSelectedKey = (key: string) => {
    setSelectedKeyState(key);
    if (isReservedConfigKey(key)) {
      setKeyValidationMessage(RESERVED_CONFIG_KEY_MESSAGE);
      setJsonValid(false);
      return;
    }
    setKeyValidationMessage('');
  };

  const currentFormValues = useMemo(() => ({ selectedKey, formData }), [selectedKey, formData]);
  const isDirtyRaw = useUnsavedChanges(true, currentFormValues);
  const isDirty = !isNew && isDirtyRaw;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (!isNew && currentConfig) {
      setFormData(currentConfig?.value || {});
    }
  }, [currentConfig, isNew]);

  const applyDefaultTimezoneValidation = (timezoneValue: string) => {
    const timezone = timezoneValue.trim();

    if (!timezone) {
      setJsonValid(false);
      setTimezoneValidationMessage('Timezone is required.');
      return false;
    }

    if (!moment.tz.zone(timezone)) {
      setJsonValid(false);
      setTimezoneValidationMessage('Invalid timezone. Use a valid IANA value like UTC or Europe/Amsterdam.');
      return false;
    }

    setJsonValid(true);
    setTimezoneValidationMessage('');
    return true;
  };

  // Coerce / validate formData whenever the active config key changes.
  useEffect(() => {
    if (!selectedKey) return;
    if (isReservedConfigKey(selectedKey)) {
      setKeyValidationMessage(RESERVED_CONFIG_KEY_MESSAGE);
      setJsonValid(false);
      return;
    }
    setKeyValidationMessage('');

    const configType = resolveConfigType(selectedKey);
    const coerced = configType.coerceFormData?.(formData);
    const activeFormData = coerced !== undefined ? coerced : formData;
    if (coerced !== undefined) setFormData(coerced);

    configType.onActivate?.({
      formData: activeFormData,
      setJsonValid,
      setTimezoneValidationMessage,
      applyDefaultTimezoneValidation,
    });
    // eslint-disable-next-line
  }, [selectedKey]);

  const handleDefaultTimezoneChange = (value: string) => {
    setFormData((prevData: any) => ({
      ...(prevData || {}),
      timezone: value,
    }));
    applyDefaultTimezoneValidation(value);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    // The row being edited, or null on create. The row is looked up by list index, so a
    // list refresh while the dialog is open can leave an edit with no row; then there is
    // nothing to update, so say so rather than silently doing nothing.
    const editing = isNew ? null : currentConfig;
    if (!isNew && !editing) {
      notify.error('Configuration not found; the UI may be out of date. Please refresh.');
      return;
    }
    if (!jsonValid || isSubmitting) return;
    if (isReservedConfigKey(selectedKey)) {
      setKeyValidationMessage(RESERVED_CONFIG_KEY_MESSAGE);
      setJsonValid(false);
      return;
    }

    const configType = resolveConfigType(selectedKey);
    const valueToSave = configType.prepareSave?.(formData) ?? formData;
    if (configType.validateBeforeSave && !configType.validateBeforeSave(valueToSave, { applyDefaultTimezoneValidation })) {
      return;
    }

    const updatedConfig = {
      key: selectedKey,
      value: valueToSave,
    };

    // The save MUST be awaited and its outcome inspected. Dispatching and
    // closing regardless made a rejected save indistinguishable from a
    // successful one — the modal shut, the row was never written, and the
    // backend's complaint went nowhere.
    setIsSubmitting(true);
    const result = editing ? await dispatch(updateConfig({ config_id: editing.config_id, ...updatedConfig })) : await dispatch(createConfig(updatedConfig));
    setIsSubmitting(false);

    if (result.meta.requestStatus === 'rejected') {
      // The slice has already reduced the response body to a message, so the
      // fallback only covers a rejection that carried no rejectValue at all
      // (a thunk that threw before its own catch could run).
      notify.error(typeof result.payload === 'string' ? result.payload : 'Failed to save the configuration.');
      return;
    }

    handleClose();
  };

  const fieldsContext = {
    selectedKey,
    setSelectedKey,
    formData,
    setFormData,
    setJsonValid,
    jsonValid,
    onTimezoneChange: handleDefaultTimezoneChange,
    timezoneValidationMessage,
  };

  const handleConfigTypeChange = (value: string) => {
    // Type activation (seed / coerce / validate) is owned solely by the
    // selectedKey useEffect via coerceFormData + onActivate — do not seed here
    // or the two paths can silently diverge when seedOnSelect and coerce disagree.
    setSelectedKey(value);
  };

  const fields = resolveConfigType(selectedKey).renderFields(fieldsContext);

  return (
    <Modal show={true} onHide={handleRequestClose} backdrop="static" keyboard={false} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {isNew ? t('button.newe') + ' ' + t('menu.configuration') + ' ' + t('key') : t('button.edit') + ' ' + t('menu.configuration') + ' ' + t('key')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {keyValidationMessage ? <Form.Text className="text-danger d-block mb-3">{keyValidationMessage}</Form.Text> : null}
        {!isNew ? (
          <Form.Group as={Row} className="mb-3" controlId="formConfigKey">
            <Form.Label column sm="2">
              Config {t('key')}
            </Form.Label>
            <Col sm="10">
              <Form.Control type="text" defaultValue={currentConfig?.key} plaintext readOnly />
            </Col>
          </Form.Group>
        ) : (
          <Form.Group as={Row} className="mb-3" controlId="configType">
            <Form.Label column sm="2">
              Config Type
            </Form.Label>
            <Col sm="10">
              <Form.Control as="select" value={selectedKey} onChange={(e) => handleConfigTypeChange(e.target.value)}>
                <option value="">
                  {t('select')} {t('menu.configuration')} Type
                </option>
                {CONFIG_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Form.Group>
        )}
        {fields}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="phoenix-danger" onClick={handleRequestClose} startIcon={<FontAwesomeIcon icon={faWindowClose} />}>
          {t('button.close')}
        </Button>
        <Button variant="phoenix-primary" onClick={handleSave} startIcon={<FontAwesomeIcon icon={faSave} />} disabled={!jsonValid || isSubmitting}>
          {t('button.saveAndClose')}
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          handleClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EditConfigItem;
